import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Container,
  Paper,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../context/AuthContext";
import heroImage from "../Assests/Images/hero.png";

function CandidateDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [jobAlertEnabled, setJobAlertEnabled] = useState(
    user?.job_alert_subscription !== false
  );
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await api.patch("/applications/candidate/preferences/job-alerts", {
        jobAlertSubscription: jobAlertEnabled,
      });
      setToast({
        open: true,
        message: jobAlertEnabled
          ? "Job alerts enabled successfully."
          : "Job alerts disabled successfully.",
        severity: "success",
      });
    } catch {
      setToast({
        open: true,
        message: "Failed to save preferences. Please try again.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container sx={{ py: 2 }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0f2027, #203a43, #2c5364)",
          color: "white",
          p: 4,
          borderRadius: 4,
          mb: 3,
          boxShadow: 3,
        }}
      >
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: "white" }}>
              Welcome, {user?.first_name || user?.firstName || "Candidate"}!
            </Typography>
            <Typography variant="h6" sx={{ color: "white", opacity: 0.9, mb: 2 }}>
              Your personalized dashboard
            </Typography>
            <Typography variant="body1" sx={{ color: "white", opacity: 0.8 }}>
              Access all your applications and interviews in one click.
            </Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box
              component="img"
              src={heroImage}
              alt="Welcome"
              sx={{
                width: "100%",
                maxHeight: 200,
                objectFit: "contain",
                filter: "drop-shadow(0 0 10px rgba(0,255,255,0.3))",
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Quick Access Buttons */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 2 }} fontWeight="bold">
          Quick Actions
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => navigate("/my-applications")}
              sx={{
                py: 3,
                fontSize: "1.2rem",
                borderRadius: 3,
                background: "linear-gradient(135deg, #4F46E5 0%, #20B2AA 100%)",
              }}
            >
              My Applications
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => navigate("/my-interview-history")}
              sx={{
                py: 3,
                fontSize: "1.2rem",
                borderRadius: 3,
                background: "linear-gradient(135deg, #FF8C00 0%, #FFA500 100%)",
              }}
            >
              My Interviews
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Job Alert Preferences */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" sx={{ mb: 3 }} fontWeight="bold">
          Job Alert Preferences
        </Typography>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: 3,
            border: "1px solid #E2F0EE",
            background: "#FAFFFE",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            <NotificationsActiveIcon sx={{ color: "#4F46E5", fontSize: 26 }} />
            <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
              Email Job Alerts
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "#475569", mb: 3 }}>
            Receive email notifications when recruiters post relevant jobs.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={jobAlertEnabled}
                onChange={(e) => setJobAlertEnabled(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#4F46E5" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#4F46E5",
                  },
                }}
              />
            }
            label={
              <Typography variant="body1" fontWeight={500}>
                {jobAlertEnabled ? "On" : "Off"}
              </Typography>
            }
            sx={{ mb: 1 }}
          />

          <Typography
            variant="body2"
            sx={{
              color: jobAlertEnabled ? "#4338CA" : "#94A3B8",
              mb: 3,
              ml: 0.5,
              fontStyle: "italic",
            }}
          >
            {jobAlertEnabled
              ? "You will receive relevant job opportunities via email."
              : "You will not receive recruiter job notifications."}
          </Typography>

          <Button
            variant="contained"
            onClick={handleSavePreferences}
            disabled={saving}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.2,
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.95rem",
              background: "linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #4338CA 0%, #3730A3 100%)",
              },
              "&.Mui-disabled": { opacity: 0.7 },
              minWidth: 150,
            }}
          >
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Save Preferences"
            )}
          </Button>
        </Paper>
      </Box>

      {/* Toast notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default CandidateDashboard;
